<template>
  <header class="sticky top-0 z-50 shadow-lg bg-yellow-500 dark:bg-gray-900">
    <nav
      class="mx-auto flex items-center justify-between max-w-7xl p-6 lg:px-8"
      aria-label="Global"
    >
      <div class="menu-toggle lg:hidden flex items-center justify-end">
        <button
          @click="toggleMobileMenu"
          type="button"
          class="text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded"
          :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="mobileMenuOpen.toString()"
          aria-controls="mobile-menu"
        >
          <i
            :class="mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'"
            class="w-6 h-6"
          ></i>
        </button>
      </div>
      <!-- Logo -->
      <div class="flex">
        <router-link to="/" class="-m-1.5 p-1.5">
          <span class="sr-only">Your Company</span>
          <img
            src="@/assets/logo-black.webp"
            alt="beeApp logo"
            class="h-14 w-14 block dark:hidden"
            width="56"
            height="56"
            loading="eager"
            decoding="async"
          />

          <!-- Dark Mode Logo -->
          <img
            src="@/assets/logo-primary-color.webp"
            alt="beeApp logo"
            class="h-14 w-14 hidden dark:block"
            width="56"
            height="56"
            loading="eager"
            decoding="async"
          />
        </router-link>
      </div>

      <!-- Desktop Navigation -->
      <div class="hidden lg:flex lg:flex-1 lg:justify-center">
        <!-- Dropdown Buttons Container -->
        <div class="flex gap-x-8">
          <!-- Experience Dropdown -->
          <div class="relative dropdown">
            <button
              @click="toggleDropdown('experience')"
              type="button"
              class="flex items-center gap-x-1 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100"
              :aria-expanded="dropdownOpen === 'experience'"
              aria-controls="experience-menu"
            >
              Experience
              <i
                :class="
                  dropdownOpen === 'experience'
                    ? 'fas fa-chevron-up'
                    : 'fas fa-chevron-down'
                "
                class="w-5 h-5"
              ></i>
            </button>
            <div
              v-if="dropdownOpen === 'experience'"
              class="absolute z-10 mt-3 w-48 shadow-lg rounded-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-gray-800"
              id="experience-menu"
            >
              <router-link
                to="/experience-user"
                class="dropdown-item-first"
                active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
                @click="dropdownOpen = null"
                @mouseenter="handleHover"
                @mouseleave="handleLeave"
              >
                As a User
              </router-link>

              <router-link
                to="/experience-brewery"
                class="dropdown-item-last"
                active-class="bg-yellow-500 rounded-b-md text-white"
                @click="dropdownOpen = null"
              >
                As a Brewery
              </router-link>
            </div>
          </div>

          <!-- Contact Dropdown -->
          <div class="relative dropdown">
            <button
              @click="toggleDropdown('contact')"
              type="button"
              class="flex items-center gap-x-1 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100"
              :aria-expanded="dropdownOpen === 'contact'"
              aria-controls="contact-menu"
            >
              Contact
              <i
                :class="
                  dropdownOpen === 'contact'
                    ? 'fas fa-chevron-up'
                    : 'fas fa-chevron-down'
                "
                class="w-5 h-5"
              ></i>
            </button>
            <div
              v-if="dropdownOpen === 'contact'"
              class="absolute z-10 mt-3 w-48 bg-white dark:bg-gray-800 shadow-lg rounded-lg ring-1 ring-black ring-opacity-5"
              id="contact-menu"
            >
              <router-link
                to="/about"
                class="dropdown-item-first"
                active-class="bg-yellow-500 rounded-t-md text-white"
                @click="dropdownOpen = null"
              >
                About Us
              </router-link>
              <a
                href="mailto:contact@beeforbeer.com"
                class="dropdown-item-last"
                @click="dropdownOpen = null"
              >
                Contact Us
              </a>
            </div>
          </div>
          <router-link
            to="/pricing"
            class="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100"
            active-class="underline text-gray-900"
          >
            Pricing
          </router-link>
          <div class="flex items-center space-x-4 lg:justify-end">
            <!-- Onboard Your Brewery -->
            <router-link
              to="/onboarding"
              class="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-x-2 transition-colors duration-300 hover:underline"
              active-class="underline text-gray-900"
            >
              Onboard Your Brewery
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Dark Mode Toggle -->
      <div class="flex items-center space-x-4 lg:justify-end">
        <button
          type="button"
          class="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 lg:h-12 lg:w-12 transition-transform transform hover:scale-110"
          @click="toggleDarkMode"
          :aria-pressed="darkMode"
          :aria-label="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <!-- Light Mode Icon -->
          <img
            v-if="!darkMode"
            :src="require('@/assets/dark-mode.png')"
            alt=""
            class="h-10 w-auto sm:h-12 sm:w-auto lg:h-12 lg:w-auto"
            width="161"
            height="346"
            loading="eager"
            decoding="async"
          />
          <!-- Dark Mode Icon -->
          <img
            v-else
            :src="require('@/assets/light-mode.png')"
            alt=""
            class="h-10 w-auto sm:h-12 sm:w-auto lg:h-12 lg:w-auto"
            width="162"
            height="346"
            loading="eager"
            decoding="async"
          />
        </button>
      </div>
    </nav>

    <!-- Mobile Dropdown Menu -->
    <div
      v-if="mobileMenuOpen"
      class="mobile-menu absolute top-full left-0 w-full bg-white dark:bg-gray-900 shadow-lg z-50 p-4 transition-all rounded-b-lg"
      id="mobile-menu"
    >
      <router-link
        to="/experience-user"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
        active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
      >
        Experience as a User
      </router-link>
      <router-link
        to="/experience-brewery"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
        active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
      >
        Experience as a Brewery
      </router-link>
      <router-link
        to="/about"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
        active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
      >
        About Us
      </router-link>
      <a
        href="mailto:contact@beeforbeer.com"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
      >
        Contact Us
      </a>
      <router-link
        to="/pricing"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
        active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
      >
        Pricing
      </router-link>
      <router-link
        to="/onboarding"
        class="block px-4 py-2 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
        @click="closeMenu"
        active-class="bg-yellow-500 dark:bg-yellow-500 text-white"
      >
        Onboard Your Brewery
      </router-link>
    </div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useDarkModeStore } from "@/stores/darkMode";

export default {
  name: "WebHeader",
  setup() {
    const darkModeStore = useDarkModeStore();
    const mobileMenuOpen = ref(false);
    const dropdownOpen = ref(null); // Tracks which dropdown is open

    // Computed property for dark mode from store
    const darkMode = computed(() => darkModeStore.isDark);

    // Toggle dark mode using store
    const toggleDarkMode = () => {
      darkModeStore.toggle();
    };

    // Toggle mobile menu
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
    };

    // Toggle dropdown menu
    const toggleDropdown = (dropdown) => {
      dropdownOpen.value = dropdownOpen.value === dropdown ? null : dropdown;
    };

    // Close dropdown when clicking outside
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".dropdown")) {
        dropdownOpen.value = null; // Close the dropdown
      }
      if (
        mobileMenuOpen.value &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".menu-toggle")
      ) {
        mobileMenuOpen.value = false;
      }
    };

    // Add and remove event listeners
    onMounted(() => {
      document.addEventListener("mousedown", handleOutsideClick);
    });

    onUnmounted(() => {
      document.removeEventListener("mousedown", handleOutsideClick);
    });

    const closeMenu = () => {
      mobileMenuOpen.value = false;
      dropdownOpen.value = null;
    };

    return {
      mobileMenuOpen,
      darkMode,
      dropdownOpen,
      toggleDarkMode,
      toggleMobileMenu,
      toggleDropdown,
      closeMenu,
    };
  },
};
</script>
